import { useState, useContext, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Waypoint } from 'react-waypoint'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Helpers
import { oneTimeTagEventDispatcher } from '../../../../helpers/tagManager'
import { useAmplitudeDebounceSetters } from '../../../../hooks/tracking'

// Constants
import {
  ScrolledIntoView,
  EVENTS_LABELS,
} from '../../../../constants/trackingEvents'

// Components
import { RefiDetailsOverlay } from '../../../homeowners/RefiDetailsOverlay/RefiDetailsOverlay'
import Text from '../../Text'
import RefiHeading from '../../RefiHeading'
import YearsInHomeSlider from './YearsInHomeSlider'
import { RefiOptionReveal } from './RefiOptionReveal'

// Styles
import styles from './RefiByYearsInHome.module.scss'

const propTypes = {
  className: PropTypes.string,
  yearsInHome: PropTypes.number,
  fha: PropTypes.bool,
  isConsolidation: PropTypes.bool,
  refiDetails: PropTypes.array,
  navRefiOption: PropTypes.func,
  setYearsInHome: PropTypes.func,
  locked: PropTypes.bool,
}

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.RefiScenariosSection,
})

const RefiByYearsInHome = ({
  className,
  fha,
  isConsolidation,
  refiDetails,
  yearsInHome,
  navRefiOption,
  setYearsInHome,
  locked,
}) => {
  const [years, setYears] = useState(yearsInHome)
  const [mouseDown, setMouseDown] = useState(false)
  const { ampli } = useContext(AmplitudeContext)

  const [trackingYearsInHomeAdjusted, clearDebounceForYearsInHomeAdjusted] =
    useAmplitudeDebounceSetters(() => ampli?.yearsInHomeAdjusted())
  const [trackModuleViewed, clearDebounceForTrackModuleViewed] =
    useAmplitudeDebounceSetters(() =>
      ampli?.moduleViewed({ module_name: AmplitudeEventConstants.REFI })
    )

  const onSliderChange = useCallback(
    e => {
      const value = parseInt(e.target.value, 10)
      mouseDown ? setYears(value) : setYearsInHome(value)
      trackingYearsInHomeAdjusted()
    },
    [mouseDown, setYears, setYearsInHome, trackingYearsInHomeAdjusted]
  )

  const onSliderBlur = useCallback(() => {
    clearDebounceForYearsInHomeAdjusted()
  }, [clearDebounceForYearsInHomeAdjusted])

  const handleScrollEvent = useCallback(() => {
    scrollIntoView()
    trackModuleViewed()
  }, [trackModuleViewed])

  useEffect(() => {
    return () => {
      clearDebounceForTrackModuleViewed()
    }
  }, [clearDebounceForTrackModuleViewed])

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classnames(styles.root, className)}
      onMouseUp={() => {
        setMouseDown(false)
        if (years !== yearsInHome) {
          setYearsInHome(years)
        }
      }}
    >
      <Text
        variant='neutralCool'
        variantCount='700'
        align='center'
        size='s'
        upperspaced
      >
        <FormattedMessage
          id='RefiByYearsInHome.subHeading.new'
          defaultMessage='Rates Based On National Trends'
        />
      </Text>
      <RefiHeading
        isFha={!!fha}
        isConsolidation={isConsolidation}
        className={styles.heading}
      />
      <Waypoint onEnter={handleScrollEvent} bottomOffset='50%'>
        <div />
      </Waypoint>
      <YearsInHomeSlider
        min='1'
        max='30'
        value={mouseDown ? years : yearsInHome}
        onMouseDown={() => setMouseDown(true)}
        onChange={onSliderChange}
        onBlur={onSliderBlur}
      />
      <RefiDetailsOverlay options={refiDetails} />
      <RefiOptionReveal
        yearsInHome={yearsInHome}
        onClickOption={navRefiOption}
        options={refiDetails}
        locked={locked}
      />
    </div>
  )
}

RefiByYearsInHome.propTypes = propTypes

export default RefiByYearsInHome
